import React, { lazy, Suspense, useCallback, useContext } from "react";

import { LanguageContext } from "./LanguageContext"; // Import the LanguageContext
import "./Home.css";
import { faqs } from "./data/faqs";
import { images } from "./data/images";
import SEO from "../src/components/SEO";



const FAQ = lazy(() => import("./components/FAQ")); // Lazy Loading FAQ
const ImageButtonSections = lazy(() => import("./components/ImageButtonSections")); // Lazy Load

const Home = () => {
  const { language } = useContext(LanguageContext); // Get the current language

  const handleShare = useCallback((imageUrl) => {
    if (navigator.share) {
      navigator
        .share({
          title: language === "english"
            ? "Check out this amazing image!"
            : "इस अद्भुत तस्वीर को देखें!",
          text: language === "english"
            ? "Here is an image you might like!"
            : "यहां एक तस्वीर है जो आपको पसंद आ सकती है!",
          url: imageUrl,
        })
        .then(() => console.log("Successfully shared"))
        .catch((error) => console.log("Error sharing:", error));
    } else {
      const url = encodeURIComponent(imageUrl);
      const message = encodeURIComponent(
        language === "english"
          ? "Check out this amazing image!"
          : "इस अद्भुत तस्वीर को देखें!"
      );
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${message}`,
        "_blank"
      );
      window.open(
        `https://twitter.com/intent/tweet?url=${url}&text=${message}`,
        "_blank"
      );
      window.open(
        `https://api.whatsapp.com/send?text=${message} ${url}`,
        "_blank"
      );
    }
  }, [language]);

// SEO Metadata
const metaTitle = "Snapseed QR Codes - Free Photo Editing Presets & Filters";
const metaDescription =
  "Discover 500+ must-have Snapseed QR codes for stunning photo edits. Download free presets and filters for DSLR-like results!";
const metaUrl = "https://www.snapseedqrcodes.download/";
const metaImage = "https://www.snapseedqrcodes.download/snapseedqrcodes_logo.webp";

// Schema Markup
const schemaMarkup = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Snapseed QR Codes",
  url: metaUrl,
  description: metaDescription,
  publisher: {
    "@type": "Organization",
    name: "Snapseed QR Codes",
    logo: metaImage,
  },
    "datePublished": "2025-01-29",
    "dateModified": "2025-05-07"
};

  return (
    <div className="home-page">

    {/* SEO Optimized Meta Tags */}
    <SEO
        title={metaTitle}
        description={metaDescription}
        url={metaUrl}
        image={metaImage}
        schemaMarkup={schemaMarkup}
      />
     
      {/* Hero Section */}
      <section className="hero-section">
    
      <h1 className="hero-title">
    {language === "english" ? (
      <>
        55+ Must-Have Snapseed QR Codes: Your Complete Guide &{" "}
        <a href="/collection" className="hero-link">
          Collection
        </a>
      </>
    ) : (
      <>
        55+ ज़रूरी स्नैपसीड क्यूआर कोड: आपकी पूरी गाइड और{" "}
        <a href="/collection" className="hero-link">
          संग्रह
        </a>
      </>
    )}
  </h1>
      </section>

      {/* Snapseed QR Code Overview */}
      <section className="snapseed">
        <h2>
          {language === "english"
            ? "Understanding Snapseed QR Codes"
            : "Snapseed QR कोड को समझना"}
        </h2>
        <p>
          {language === "english"
            ? "Snapseed QR codes are a revolutionary method for photo editing. They provide an easy way to store and share your favorite edits."
            : "Snapseed QR कोड्स फोटो एडिटिंग का एक क्रांतिकारी तरीका हैं। ये आपको आपकी पसंदीदा एडिट्स को स्टोर और शेयर करने का आसान विकल्प देते हैं।"}
        </p>

        <h2>
          {language === "english"
            ? "What is a Snapseed QR Code?"
            : "Snapseed QR कोड क्या है?"}
        </h2>
        <p>
          {language === "english"
            ? `Think of a Snapseed QR code like a recipe card that remembers your photo edits. It is a special square pattern that saves the complete method of editing your picture. All your favorite filters, colors, and touch-ups are secured in this simple code. The best part? You can use this code repeatedly. Just scan the QR code with your camera, and Snapseed will apply those beautiful edits to your new photo.`
            : `Snapseed QR कोड को एक रेसिपी कार्ड की तरह समझें जो आपकी फोटो एडिट्स को याद रखता है। यह एक खास स्क्वायर पैटर्न है जो आपकी तस्वीर को एडिट करने का पूरा तरीका सेव करता है। आपके सभी पसंदीदा फ़िल्टर, रंग और टच-अप्स इस साधारण कोड में सुरक्षित हो जाते हैं। सबसे अच्छी बात? आप इस कोड का बार-बार उपयोग कर सकते हैं। बस अपने कैमरे से QR कोड को स्कैन करें, और Snapseed आपके नई फोटो पर वही सुंदर एडिट्स लगा देगा।`}
        </p>
      </section>
      <hr />

      {/* Making the Most of Snapseed QR Codes */}
      <section className="snapseed">
        <h2>
          {language === "english"
            ? "Making the Most of Snapseed QR Codes"
            : "Snapseed QR कोड्स का सर्वश्रेष्ठ उपयोग करना"}
        </h2>
        <p>
          {language === "english"
            ? "By using Snapseed QR codes effectively, you can save both time and effort in your editing process. These codes make the editing process easier and more enjoyable."
            : "Snapseed QR कोड्स का सही उपयोग करके आप अपनी एडिटिंग में समय और मेहनत दोनों बचा सकते हैं। ये कोड आपकी एडिटिंग प्रक्रिया को आसान और मजेदार बना देते हैं।"}
        </p>

        <h2>
          {language === "english"
            ? "Why You'll Love Snapseed QR Codes"
            : "Snapseed QR कोड्स से क्यों प्यार होगा?"}
        </h2>
        <p>
          {language === "english"
            ? `When you edit a lot of pictures, these QR codes become your best friend. They help you save time, allowing you to add your favorite edits with just a scan. Want to share your amazing photo style with friends? Just send them your QR code.`
            : `जब आप बहुत सारी तस्वीरें एडिट करते हैं, तो ये QR कोड्स आपके सबसे अच्छे दोस्त बन जाते हैं। ये आपको समय बचाने में मदद करते हैं, जिससे आप अपनी पसंदीदा एडिट्स को सिर्फ एक स्कैन से जोड़ सकते हैं।`}
        </p>
      </section>

      <hr />

      {/* QR Code Creation Section */}
      <section className="snapseed">
        <h2>
          {language === "english"
            ? "How to Make These QR Codes"
            : "इन QR कोड्स को कैसे बनाएं?"}
        </h2>
        <ul className="benefits-list">
          <li>
            {language === "english"
              ? "First, make your photo look perfect in Snapseed using any tools you like."
              : "Snapseed में अपनी तस्वीर को किसी भी टूल का उपयोग करके परफेक्ट बनाएं।"}
          </li>
          <li>
            {language === "english"
              ? "Tap Edit Stacks at the top right corner and look for 'QR Look'."
              : "टॉप राइट कॉर्नर पर 'Edit Stacks' टैप करें और 'QR Look' खोजें।"}
          </li>
          <li>
            {language === "english"
              ? "Click 'Create QR Look' to make your special code."
              : "'Create QR Look' पर क्लिक करके अपना विशेष कोड बनाएं।"}
          </li>
          <li>
            {language === "english"
              ? "Save this code on your phone or send it to friends"
              : "इस कोड को अपने फोन में सेव करें या दोस्तों को भेजें।"}
          </li>
          <li>
            {language === "english"
              ? "When you want to use it again, pick a new photo"
              : "जब आप इसे फिर से उपयोग करना चाहें, तो एक नई तस्वीर चुनें।"}
          </li>
          <li>
            {language === "english"
              ? "Go to QR Look, then Scan QR Look"
              : "QR Look में जाएं, फिर Scan QR Look पर क्लिक करें।"}
          </li>
          <li>
            {language === "english"
              ? "Point your camera at the code, and your new photo gets the same beautiful edits"
              : "अपने कैमरे को कोड पर पॉइंट करें, और आपकी नई तस्वीर को वही सुंदर एडिट्स मिलेंगे।"}
          </li>
        </ul>
      </section>
      <hr />
      <section className="snapseed">
        <h2>
          {language === "english"
            ? "55+ Top Snapseed QR Code Looks and Presets"
            : "55+ Top Snapseed QR Code Looks and Presets | 55+ Snapseed QR कोड लुक्स और प्रीसेट्स"}
        </h2>
        <p>
  {language === "english" ? (
    <>
      Now for the fun part! We're sharing more than 55+ great Snapseed QR looks and presets. Each one makes your photos look special—from a vintage style to a perfect sunset glow. We've sorted them by style so you can easily find the best look for your photos. 
      <a href="/collection">Check out our presets collection here.</a>
    </>
  ) : (
    <>
      अब बारी है सबसे मजेदार हिस्से की! यहां हम 60+ अद्भुत Snapseed QR लुक्स और प्रीसेट्स का संग्रह साझा कर रहे हैं। हर एक में अपनी खासियत है – आपकी तस्वीरों को विंटेज लुक देने से लेकर उन्हें परफेक्ट सनसेट ग्लो तक देने के लिए। 
      <a href="/collection">यहां हमारे प्रीसेट्स संग्रह को देखें।</a>
    </>
  )}
</p>   
        <p>
          {language === "english"
            ? `Think of this section like a cookbook for photo editing. Each QR code is like a recipe that gives your photos a unique touch. Whether you want your photos to look dreamy, sharp, vintage, or modern, we have the right code for you!`
            : `हमने इन्हें स्टाइल के अनुसार व्यवस्थित किया है ताकि आप अपनी तस्वीरों के लिए सही लुक आसानी से पा सकें।
इस सेक्शन को अपनी फोटो एडिटिंग कुकबुक की तरह समझें। हर QR कोड एक रेसिपी की तरह है जो आपकी तस्वीरों में अपना अलग फ्लेवर जोड़ता है। चाहे आप अपनी तस्वीरों को ड्रीमी, शार्प, विंटेज, या मॉडर्न लुक देना चाहें, हमारे पास आपके लिए सही कोड है!
`}
        </p>
   
     {/* Image Sections */}
     <Suspense fallback={<div>Loading images...</div>}>
     {images.map((image, index) => (
    <ImageButtonSections
      key={index}
      id={index + 1}  // ✅ Pass the index (starting from 1)
      image={image.src}
      altText={image.alt ? (language === "english" ? image.alt.en : image.alt.hi) : "default_alt_text"}
      heading={language === "english" ? image.heading.en : image.heading.hi}
      description={language === "english" ? image.description.en : image.description.hi}
      onShare={() => handleShare(image.src)}
    />
))}

          </Suspense>
          </section>
{/* Snapseed QR Code Overview */}
<section className="snapseed">
        <h2>
          {language === "english"
            ? "Using Your Snapseed QR Codes"
            : "Using Your Snapseed QR Codes (आपके स्नैपसीड क्यूआर कोड का उपयोग करना)"}
        </h2>
        <p>
          {language === "english"
            ? "Using Snapseed QR codes is very easy. It greatly enhances your photo editing experience by making it faster and more efficient."
            : "चलिए जानते हैं कि स्नैपसीड में इन शानदार क्यूआर कोड्स का उपयोग कैसे करें। यह आपको लगता है उससे भी आसान है!"}
        </p>

        <h2>
          {language === "english"
            ? "How to Apply Your QR Code"
            : "How to Apply These QR Codes (इन क्यूआर कोड्स को कैसे लागू करें)"}
        </h2>
        <ul className="benefits-list">
          <li>
            {language === "english"
              ? "First, open your new photo in Snapseed."
              : "स्नैपसीड में अपनी नई तस्वीर खोलें"}
          </li>
          <li>
            {language === "english"
              ? "Go to Edit Stack in the top right corner and tap on it."
              : "ऊपरी दायें कोने में Edit Stack पर जाएं और उसे टैप करें"}
          </li>
          <li>
            {language === "english"
              ? "Find the QR Look button and tap it."
              : "QR Look बटन ढूँढें और उसे टैप करें"}
          </li>
          <li>
            {language === "english"
              ? "Choose Scan QR Look"
              : "Scan QR Look चुनें"}
          </li>
          <li>
            {language === "english"
              ? "Hold your phone over the QR code until it scans."
              : "अपने फोन को क्यूआर कोड के ऊपर रखें जब तक कि यह स्कैन न हो जाए बस इसी तरह, "}
          </li>
          <li>
            {language === "english"
              ? "And just like that, your new photo receives all the beautiful edits!"
              : "ठीक इसी तरह, आपकी नई तस्वीर में सभी खूबसूरत संपादन हो जाते हैं!--चिंता न करें, अगर आप इसे और भी बेहतर बनाना चाहते हैं, तो आप QR कोड का उपयोग करने के बाद छोटे-छोटे बदलाव कर सकते हैं - आपकी मूल फ़ोटो सुरक्षित रहेगी। इसे रेसिपी के साथ खाना पकाने जैसा समझें। QR कोड आपकी रेसिपी है, लेकिन आप चाहें तो थोड़ा और नमक (या इस मामले में, थोड़ा और चमकीला) डाल सकते हैं!"}


          </li>
         
        </ul>
        <h2>
    {language === "english"
      ? "Let's Wrap Up What We Learned!"
      : "आइए जानें हमने क्या सीखा!"}
  </h2>
  <p>
    {language === "english"
      ? "You have now become a Snapseed QR code expert! These small patterns act like magical recipes for your photos. When you scan them, they automatically enhance your pictures wonderfully."
      : "अब आप एक स्नैपसीड क्यूआर कोड विशेषज्ञ बन गए हैं! ये छोटे पैटर्न आपकी तस्वीरों के लिए जादुई रेसिपी की तरह काम करते हैं। जब आप उन्हें स्कैन करते हैं, तो ये स्वचालित रूप से आपकी तस्वीरों को अद्भुत तरीके से बेहतर बनाते हैं।"}
  </p>

  <h3>
    {language === "english" ? "Here's why they are so special:" : "यह इतने खास क्यों हैं:"}
  </h3>
  <ul className="benefits-list">
    <li>
      {language === "english"
        ? "They save your time by remembering all your favorite editing steps."
        : "ये आपके सभी पसंदीदा संपादन चरणों को याद रखकर आपका समय बचाते हैं।"}
    </li>
    <li>
      {language === "english"
        ? "They help give all your photos a uniformly beautiful look."
        : "ये आपकी सभी तस्वीरों को समान रूप से सुंदर रूप देने में मदद करते हैं।"}
    </li>
    <li>
      {language === "english"
        ? "You can share them with friends who are enthusiastic about photography."
        : "आप इन्हें फोटोग्राफी के शौकीन दोस्तों के साथ साझा कर सकते हैं।"}
    </li>
  </ul>

  <h3>
    {language === "english"
      ? "The best part? You can create QR codes for any type of photo:"
      : "सबसे अच्छी बात? आप किसी भी प्रकार की तस्वीर के लिए क्यूआर कोड बना सकते हैं:"}
  </h3>
  <ul className="benefits-list">
    <li>
      {language === "english"
        ? "Perfect portraits that showcase people in their best form."
        : "परफेक्ट पोर्ट्रेट्स जो लोगों को उनके सर्वश्रेष्ठ रूप में दिखाते हैं।"}
    </li>
    <li>
      {language === "english"
        ? "Stunning sunsets that capture all the beautiful colors."
        : "शानदार सूर्यास्त जो सभी खूबसूरत रंगों को कैद करता है।"}
    </li>
    <li>
      {language === "english"
        ? "Classic film looks that give your photos a timeless feel."
        : "क्लासिक फिल्म लुक्स जो आपकी तस्वीरों को एक सदाबहार एहसास देते हैं।"}
    </li>
  </ul>
      </section>
      <hr />


      <Suspense fallback={<div>Loading FAQs...</div>}>
        <FAQ faqs={faqs} />
      </Suspense>
    </div>
  );
};

export default Home;